/**
 * Survives a batch-detail recompute across a page reload. A recompute's lifecycle
 * is otherwise mutation-only state that a reload drops — while the backend Temporal
 * workflow keeps running — so we stash the active `runId` (keyed by kind + batch
 * workflow id) and re-poll it on the next mount. All access is wrapped: a blocked
 * localStorage (private mode / quota) just degrades to no resume-on-reload.
 *
 * `insights` = Performance Signals recompute; `coverage` = Coverage Signals recompute.
 */
export type RecomputeKind = 'insights' | 'coverage';

const KEY_PREFIX = 'catalog-ops:recompute:';

function storageKey(kind: RecomputeKind, workflowId: string): string {
  return `${KEY_PREFIX}${kind}:${workflowId}`;
}

/** Records the in-flight recompute's runId for a batch (called right after the POST). */
export function savePendingRecompute(kind: RecomputeKind, workflowId: string, runId: string): void {
  try {
    localStorage.setItem(storageKey(kind, workflowId), runId);
  } catch {
    // Storage unavailable — resume-on-reload simply won't kick in.
  }
}

/** Reads the pending recompute runId for a batch, or null when none is stored. */
export function readPendingRecompute(kind: RecomputeKind, workflowId: string): string | null {
  try {
    return localStorage.getItem(storageKey(kind, workflowId));
  } catch {
    return null;
  }
}

/** Clears the pending recompute marker once the run reaches a terminal state. */
export function clearPendingRecompute(kind: RecomputeKind, workflowId: string): void {
  try {
    localStorage.removeItem(storageKey(kind, workflowId));
  } catch {
    // no-op
  }
}
